import { useEffect, useRef, useState } from 'react';
import { Quiz, QuizAnswers, QuizAttempt, QuizQuestionResult } from '../../types/elearning';
import { elearningAPI } from '../../services/elearningApi';
import QuizQuestionComponent from './QuizQuestion';
import Button from '../ui/Button';
import Card from '../ui/Card';
import ToastContainer from '../ui/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface QuizComponentProps {
  quiz: Quiz;
  enrollmentId?: string;
  onPassed?: () => void;
}

function formatRemaining(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

function formatAttemptTime(attempt: QuizAttempt): string {
  const value = attempt.completedAt || attempt.startedAt;
  return new Date(value).toLocaleString();
}

export default function QuizComponent({ quiz, enrollmentId, onPassed }: QuizComponentProps) {
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [attemptNonce, setAttemptNonce] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(
    quiz.timeLimit ? quiz.timeLimit * 60 : null
  );
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [result, setResult] = useState<{
    score: number;
    passed: boolean;
    questionResults: QuizQuestionResult[];
  } | null>(null);
  const { toasts, error: showError, removeToast } = useToast();
  const submitLock = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    let cancelled = false;
    elearningAPI
      .fetchQuizAttempts(quiz.id)
      .then((list) => {
        if (!cancelled) setAttempts(list);
      })
      .catch(() => {
        if (!cancelled) setAttempts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [quiz.id]);

  const handleSubmit = async (fromTimer = false) => {
    if (submitLock.current || submitted) return;
    if (!enrollmentId) {
      showError('Enroll in the course before taking the quiz.');
      return;
    }
    if (!fromTimer && !quiz.questions.every((question) => answersRef.current[question.id] !== undefined)) {
      return;
    }

    submitLock.current = true;
    try {
      setSubmitting(true);
      const attempt = await elearningAPI.submitQuizAttempt(quiz.id, answersRef.current);
      setResult({
        score: attempt.score,
        passed: attempt.passed,
        questionResults: attempt.questionResults ?? [],
      });
      setSubmitted(true);
      setAttempts((current) => [attempt, ...current.filter((item) => item.id !== attempt.id)]);
      if (attempt.passed) {
        onPassed?.();
      }
    } catch (err) {
      submitLock.current = false;
      showError(err instanceof Error ? err.message : 'Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  useEffect(() => {
    if (!quiz.timeLimit || submitted) return;
    const total = quiz.timeLimit * 60;
    const started = Date.now();
    setRemainingSeconds(total);
    setTimedOut(false);
    const id = window.setInterval(() => {
      const left = Math.max(0, total - Math.floor((Date.now() - started) / 1000));
      setRemainingSeconds(left);
      if (left === 0) {
        window.clearInterval(id);
        setTimedOut(true);
        void handleSubmitRef.current(true);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [quiz.timeLimit, quiz.id, attemptNonce, submitted]);

  const handleRetake = () => {
    submitLock.current = false;
    setAnswers({});
    setResult(null);
    setSubmitted(false);
    setTimedOut(false);
    setRemainingSeconds(quiz.timeLimit ? quiz.timeLimit * 60 : null);
    setAttemptNonce((value) => value + 1);
  };

  const allQuestionsAnswered = quiz.questions.every((question) => answers[question.id] !== undefined);
  const sortedQuestions = [...quiz.questions].sort((a, b) => a.order - b.order);
  const locked = timedOut || submitting;

  return (
    <Card className="mb-8">
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{quiz.title}</h2>
        {quiz.timeLimit && remainingSeconds !== null && !submitted && (
          <p className={`text-sm mb-4 ${remainingSeconds <= 30 ? 'text-error font-medium' : 'text-gray-600'}`}>
            Time left: {formatRemaining(remainingSeconds)}
            {timedOut ? ' — submitting your attempt' : ''}
          </p>
        )}
        <p className="text-sm text-gray-600 mb-6">
          Passing score: {quiz.passingScore}%
        </p>

        {attempts.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Attempts</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              {attempts.map((attempt) => (
                <li key={attempt.id}>
                  {formatAttemptTime(attempt)} — {attempt.score}% — {attempt.passed ? 'Passed' : 'Failed'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!submitted ? (
          <>
            <div className="space-y-6 mb-6">
              {sortedQuestions.map((question) => (
                <QuizQuestionComponent
                  key={question.id}
                  question={question}
                  value={answers[question.id]}
                  disabled={locked}
                  onChange={(answer) => {
                    if (locked) return;
                    setAnswers((prev) => ({
                      ...prev,
                      [question.id]: answer,
                    }));
                  }}
                  showExplanation={false}
                />
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                {Object.keys(answers).length} of {quiz.questions.length} questions answered
              </p>
              <Button
                variant="primary"
                onClick={() => void handleSubmit(false)}
                disabled={!allQuestionsAnswered || locked}
              >
                {submitting ? 'Submitting...' : 'Submit Quiz'}
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div
              className={`p-4 rounded-lg ${
                result?.passed
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                {result?.passed ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                )}
                <h3 className="text-lg font-semibold">
                  {result?.passed ? 'Quiz Passed!' : 'Quiz Failed'}
                </h3>
              </div>
              <p className="text-sm">
                Your score: <strong>{result?.score}%</strong> (Passing: {quiz.passingScore}%)
              </p>
              {!result?.passed && (
                <div className="mt-4">
                  <Button variant="secondary" onClick={handleRetake}>
                    Try again
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {sortedQuestions.map((question) => {
                const userAnswer = answers[question.id];
                const questionResult = result?.questionResults.find(
                  (entry) => entry.questionId === question.id
                );
                const isCorrect = questionResult?.correct ?? false;
                const answerLabel =
                  userAnswer === true ? 'True' : userAnswer === false ? 'False' : String(userAnswer ?? '');

                return (
                  <div
                    key={question.id}
                    className={`p-4 rounded-lg border-2 ${
                      isCorrect
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {isCorrect ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-2">{question.question}</p>
                        <p className="text-sm text-gray-600 mb-2">
                          Your answer: <strong>{answerLabel || 'No answer'}</strong>
                        </p>
                        {!isCorrect && questionResult?.correctAnswer && (
                          <p className="text-sm text-gray-600 mb-2">
                            Correct answer: <strong>{questionResult.correctAnswer}</strong>
                          </p>
                        )}
                        {questionResult?.explanation && (
                          <p className="text-sm text-gray-700 mt-2 italic">
                            {questionResult.explanation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </Card>
  );
}
